import { FormControl } from '@angular/forms';

export class CustomValidators {
  static CPFValidator(control: FormControl): { error: boolean; CPF: any } {
    const mod11 = (num) => num % 11;
    const NOT = (x) => !x;
    const isEqual = (a) => (b) => b === a;
    const mergeDigits = (num1, num2) => `${num1}${num2}`;
    const getTwoLastDigits = (cpf) => `${cpf[9]}${cpf[10]}`;
    const getCpfNumeral = (cpf) => cpf.substr(0, 9).split('');
    const isRepeatingChars = (str) => str.split('').every((elem) => elem === str[0]);
    const toSumOfProducts = (multiplier: number) => (result: number, num) => result + num * multiplier--;
    const getSumOfProducts = (list, multiplier: number) => list.reduce(toSumOfProducts(multiplier), 0);
    const getDigit = (num) => (num > 1 ? 11 - num : 0);
    const getValidationDigit = (multiplier: number) => (cpf) => getDigit(mod11(getSumOfProducts(cpf, multiplier)));

    const isRepeatingNumbersCpf = isRepeatingChars;

    const isValidCPF = (cpf) => {
      const CPF = getCpfNumeral(cpf);
      const firstDigit = getValidationDigit(10)(CPF);
      const secondDigit = getValidationDigit(11)(CPF.concat(firstDigit));

      return isEqual(getTwoLastDigits(cpf))(mergeDigits(firstDigit, secondDigit));
    };

    const validate = (CPF) => NOT(isRepeatingNumbersCpf(CPF)) && isValidCPF(CPF);

    return validate(control.value.replace(/\D/g, '')) ? null : { error: true, CPF: control.value };
  }

  static CNPJValidator(control: FormControl): { error: boolean; CNPJ: any } {
    const BLACKLIST: Array<string> = [
      '00000000000000',
      '11111111111111',
      '22222222222222',
      '33333333333333',
      '44444444444444',
      '55555555555555',
      '66666666666666',
      '77777777777777',
      '88888888888888',
      '99999999999999',
    ];

    const STRICT_STRIP_REGEX = /[-\\/.]/g;
    const LOOSE_STRIP_REGEX = /[^\d]/g;

    type verifierDigit = (digits: string) => number;
    type strip = (number: string, strict?: boolean) => string;
    type isValid = (number: string, strict?: boolean) => boolean;

    const verifierDigit: verifierDigit = (digits: string): number => {
      let index = 2;
      const reverse: Array<number> = digits.split('').reduce((buffer, number) => {
        return [parseInt(number, 10)].concat(buffer);
      }, []);

      const sum: number = reverse.reduce((buffer, number) => {
        buffer += number * index;
        index = index === 9 ? 2 : index + 1;
        return buffer;
      }, 0);

      const mod: number = sum % 11;
      return mod < 2 ? 0 : 11 - mod;
    };

    const strip: strip = (number: string, strict?: boolean): string => {
      const regex: RegExp = strict ? STRICT_STRIP_REGEX : LOOSE_STRIP_REGEX;
      return (number || '').replace(regex, '');
    };

    const isValid: isValid = (number: string, strict?: boolean): boolean => {
      const stripped: string = strip(number, strict);

      // CNPJ must be defined
      if (!stripped) {
        return false;
      }

      // CNPJ must have 14 chars
      if (stripped.length !== 14) {
        return false;
      }

      // CNPJ can't be blacklisted
      if (BLACKLIST.indexOf(stripped) !== -1) {
        return false;
      }

      let numbers: string = stripped.substr(0, 12);
      numbers += verifierDigit(numbers);
      numbers += verifierDigit(numbers);

      return numbers.substr(-2) === stripped.substr(-2);
    };

    return isValid(<string>control.value) ? null : { error: true, CNPJ: control.value };
  }

  static CPForCNPJValidator(control: FormControl): { error: boolean; CNPJ: any } {
    const validCPF = CustomValidators.CPFValidator(control);
    return validCPF && validCPF.error === true ? CustomValidators.CNPJValidator(control) : null;
  }
}
